/**
 * CreateBrandSpaceModal
 *
 * Action #2 in the SpeedDialFab — creates a new client workspace under the
 * agency. POSTs all four form fields to /api/agency/workspaces:
 *   name           → Team.name
 *   ownerUserId    → Team.ownerUserId (assigned brand lead)
 *   lifecycleStage → Team.lifecycleStage (ONBOARDING / ACTIVE / …)
 *   notes          → AiBrandProfile.brandVoice (initial voice/context for AI)
 *
 * Editorial aesthetic matches StatusUpdateComposeModal: overline + Wagner
 * title + hairline sections + sticky footer.
 */

import { useGatingStore } from '@/stores/gating.store';
import { LockBadge } from '@/components/gating/LockBadge';
import { getPlanDisplayName } from '@/components/gating/gatingCopy';
import { usePortfolioLocks } from '@/hooks/usePortfolioLocks';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { agencyRoleLabelKey } from '@/utils/agencyRole';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { createWorkspace } from '@/services/agency.service';
import { type LifecycleStage } from '@/data/portfolio.types';
import { useAgencyStaff } from '@/hooks/usePortfolio';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { avatarTextClass } from '@/utils/avatarContrast';
import { lifecycleStageLabel } from '@/utils/portfolioLabels';
import { lockBodyScroll } from '@/hooks/useBodyScrollLock';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { parseBrandSpaceIssue, type BrandSpaceIssue } from '@/utils/brandSpaceErrors';
import { useBrandSpaceUsage } from '@/hooks/useBrandSpaceUsage';
import { BrandSpaceIssueNotice } from '@/components/portfolio/BrandSpaceIssueNotice';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CreateBrandSpaceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface LifecycleOption {
  value: LifecycleStage;
  bg: string;
  text: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const LIFECYCLE_OPTIONS: LifecycleOption[] = [
  { value: 'onboarding', bg: 'bg-peach/30', text: 'text-coral-700' },
  { value: 'active', bg: 'bg-primary-50', text: 'text-primary-700' },
  { value: 'paused', bg: 'bg-gray-100', text: 'text-gray-600' },
  { value: 'churning', bg: 'bg-coral-50', text: 'text-coral-700' },
];

interface LockedFieldRowProps {
  /** Portfolio feature key (agency package) */
  feature: 'portfolio_brand_lead' | 'portfolio_client_relationship';
  label: string;
}

/** A form field the agency package lacks: compact row (label, "Available on {plan}", lock). Never submits a value. */
const LockedFieldRow = ({ feature, label }: LockedFieldRowProps) => {
  const { t } = useTranslation('common');
  const openUpsell = useGatingStore((state) => state.openUpsell);
  const slug = useGatingStore((state) => state.lockedSlugs[feature] ?? null);
  const planName = getPlanDisplayName(slug);
  return (
    <button
      type="button"
      data-testid={`locked-entry-${feature}-create`}
      aria-label={t('gating_locked_label', { feature: label })}
      onClick={() => openUpsell({ feature, upsellPlanSlug: slug }, true)}
      className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-left transition-colors hover:border-primary-300 focus:outline-none focus-visible:border-primary-600"
    >
      <span className="min-w-0">
        <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
        <span className="block text-[11px] font-medium text-primary-700 truncate">
          {planName ? t('gating_available_on', { plan: planName }) : t('gating_available_on_generic')}
        </span>
      </span>
      <LockBadge />
    </button>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

export const CreateBrandSpaceModal = ({ isOpen, onClose }: CreateBrandSpaceModalProps) => {
  const { isLocked } = usePortfolioLocks();
  const leadLocked = isLocked('portfolio_brand_lead');
  const stageLocked = isLocked('portfolio_client_relationship');
  const { t } = useTranslation('agency');
  const firstInputRef = useRef<HTMLInputElement>(null);
  const { data: agencyStaff = [] } = useAgencyStaff();

  // ── Form state ──
  const [brandName, setBrandName] = useState('');
  const [ownerId, setOwnerId] = useState<number>(0);
  const [lifecycleStage, setLifecycleStage] = useState<LifecycleStage>('onboarding');
  const [notes, setNotes] = useState('');
  const [issue, setIssue] = useState<BrandSpaceIssue | null>(null);
  // Allowance up front: null while loading / unlimited-unreported -> form shown as before
  const usage = useBrandSpaceUsage(isOpen);
  const isFull = usage !== null && usage.free === 0;
  const upfrontIssue: BrandSpaceIssue | null = isFull
    ? { kind: 'full', used: usage.used, total: usage.total }
    : null;
  const shownIssue = issue ?? upfrontIssue;

  // ── Query client for cache invalidation ──
  const queryClient = useQueryClient();
  const { teamId: effectiveTeamId } = useEffectiveTeamId();

  // ── Create workspace mutation ──
  const createMutation = useMutation({
    mutationFn: createWorkspace,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agency', 'portfolio'] });
      void queryClient.invalidateQueries({ queryKey: teamQueryKeys.package.team(effectiveTeamId) });
      showToast(
        t('fab_create_brand_success', { defaultValue: 'Brand created' }),
        'success'
      );
      handleClose();
    },
    onError: (err: Error) => {
      // At the Brand Space limit: inline explanation + upsell instead of a raw error toast
      const brandIssue = parseBrandSpaceIssue(err);
      if (brandIssue) {
        setIssue(brandIssue);
        return;
      }
      showToast(
        t('fab_create_brand_error', {
          defaultValue: 'Could not create brand: {{msg}}',
          msg: err.message,
        }),
        'error'
      );
    },
  });

  // ── Focus first input on open ──
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => firstInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // ── Reset on close ──
  const handleClose = () => {
    setBrandName('');
    setOwnerId(agencyStaff[0]?.id ?? 0);
    setLifecycleStage('onboarding');
    setNotes('');
    setIssue(null);
    onClose();
  };

  // ── ESC key + body scroll lock ──
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) handleClose();
    };
    document.addEventListener('keydown', handler);
    const unlockScroll = lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', handler);
      unlockScroll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Unsaved-work guard: dirty while open and any text field has content ──
  useUnsavedGuard(isOpen && (brandName.trim().length > 0 || notes.trim().length > 0), 'create-brand-space');

  // ── Submit ──
  const handleCreate = () => {
    const trimmed = brandName.trim();
    if (!trimmed || isFull) return;
    // Map the modal's lowercase frontend lifecycle ('onboarding') to the
    // uppercase enum the backend expects ('ONBOARDING').
    const lifecycleEnum = lifecycleStage.toUpperCase() as
      | 'ONBOARDING'
      | 'ACTIVE'
      | 'CHURNING'
      | 'PAUSED';
    const trimmedNotes = notes.trim();
    setIssue(null);
    createMutation.mutate({
      name: trimmed,
      // Lead and lifecycle stage are package features: left out when the agency package lacks them
      ...(leadLocked ? {} : { ownerUserId: ownerId > 0 ? ownerId : null }),
      ...(stageLocked ? {} : { lifecycleStage: lifecycleEnum }),
      // Persist setup-time notes onto the team's brand-voice field. brandVoice
      // is the closest existing column on Team — used for AI generation context.
      ...(trimmedNotes ? { brandVoice: trimmedNotes } : {}),
    });
  };

  const canCreate = brandName.trim().length > 0 && !isFull;

  if (!isOpen) return null;

  return (
    <div
      className="fixed left-0 right-0 top-0 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:inset-0 z-50 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-brand-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="
          relative bg-white flex flex-col shadow-2xl
          w-full h-full rounded-t-2xl rounded-b-none
          sm:h-auto sm:w-[calc(100vw-2rem)] sm:max-w-xl sm:mx-0 sm:rounded-2xl
          overflow-hidden max-h-[90dvh]
        "
      >
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" aria-hidden="true" />
        </div>

        {/* ── Sticky Header ── */}
        <div className="relative shrink-0 border-b border-gray-900/10 pl-5 pr-14 py-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-medium mb-1">
            {t('fab_create_brand_overline', { defaultValue: 'Create brand' })}
          </p>
          <div>
            <h2
              id="create-brand-title"
              className="font-wagner text-2xl leading-tight tracking-tight text-gray-900"
            >
              {t('fab_create_brand_title', { defaultValue: 'New brand' })}
            </h2>
            <button
              type="button"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors outline-none border border-transparent focus-visible:border-primary-600"
              onClick={handleClose}
              aria-label={t('modal_close', { defaultValue: 'Close' })}
            >
              <i className="hm hm-cross" style={{ fontSize: 15 }} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* ── Scrollable Body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

          {shownIssue && <BrandSpaceIssueNotice issue={shownIssue} action="create" onNavigate={handleClose} />}

          {/* Brand name */}
          <section aria-labelledby="section-brand-name">
            <p
              id="section-brand-name"
              className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
            >
              {t('fab_create_brand_name_label', { defaultValue: 'Brand name' })}
              <span className="text-coral-500 ml-0.5" aria-hidden="true">*</span>
            </p>
            <Input
              ref={firstInputRef}
              id="brand-name-input"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder={t('fab_create_brand_name_placeholder', {
                defaultValue: 'e.g. Acme Corp, Studio Bloom…',
              })}
              maxLength={80}
            />
          </section>

          {/* Owner / Lead */}
          {leadLocked && (
            <LockedFieldRow
              feature="portfolio_brand_lead"
              label={t('fab_create_brand_owner_label', { defaultValue: 'Owner / Lead' })}
            />
          )}
          {!leadLocked && (
          <section aria-labelledby="section-owner">
            <p
              id="section-owner"
              className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
            >
              {t('fab_create_brand_owner_label', { defaultValue: 'Owner / Lead' })}
            </p>
            <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label={t('portfolio_create_owner_aria')}>
              {agencyStaff.map((staff) => {
                const isSelected = ownerId === staff.id;
                return (
                  <button
                    key={staff.id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setOwnerId(staff.id)}
                    className={`
                      flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-all duration-150
                      outline-none focus-visible:border-primary-600
                      ${isSelected
                        ? 'border-primary-400 bg-primary-50'
                        : 'border-gray-100 bg-gray-50 hover:border-gray-300'
                      }
                    `}
                  >
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${avatarTextClass(staff.avatarColor)} shrink-0 overflow-hidden ${staff.profilePhotoUrl ? '' : staff.avatarColor}`}
                      aria-hidden="true"
                    >
                      {staff.profilePhotoUrl ? (
                        <img
                          src={staff.profilePhotoUrl}
                          alt=""
                          className="w-full h-full rounded-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        staff.initials
                      )}
                    </span>
                    <span className="min-w-0 flex flex-col items-start leading-tight">
                      <span className={`text-xs font-semibold truncate w-full text-left ${isSelected ? 'text-primary-700' : 'text-gray-700'}`}>
                        {staff.name}
                      </span>
                      <span className="text-[10px] text-gray-400 truncate w-full text-left">{t(agencyRoleLabelKey(staff.role))}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
          )}

          {/* Lifecycle stage */}
          {stageLocked && (
            <LockedFieldRow
              feature="portfolio_client_relationship"
              label={t('fab_create_brand_lifecycle_label', { defaultValue: 'Lifecycle stage' })}
            />
          )}
          {!stageLocked && (
          <section aria-labelledby="section-lifecycle">
            <p
              id="section-lifecycle"
              className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
            >
              {t('fab_create_brand_lifecycle_label', { defaultValue: 'Lifecycle stage' })}
            </p>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('fab_create_brand_lifecycle_label')}>
              {LIFECYCLE_OPTIONS.map((opt) => {
                const isSelected = lifecycleStage === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setLifecycleStage(opt.value)}
                    className={`
                      flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold
                      border-2 transition-all duration-150
                      outline-none focus-visible:border-gray-900
                      ${isSelected
                        ? `${opt.bg} ${opt.text} border-current shadow-sm`
                        : `bg-gray-50 text-gray-600 border-transparent hover:border-gray-300`
                      }
                    `}
                  >
                    {lifecycleStageLabel(t, opt.value)}
                  </button>
                );
              })}
            </div>
          </section>
          )}

          {/* Notes */}
          <section aria-labelledby="section-notes" className="pb-2">
            <p
              id="section-notes"
              className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
            >
              {t('fab_create_brand_notes_label', { defaultValue: 'Notes' })}
              <span className="text-gray-300 ml-1 text-[10px] font-normal normal-case">
                {t('status_compose_optional', { defaultValue: 'optional' })}
              </span>
            </p>
            <Textarea
              id="brand-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('fab_create_brand_notes_placeholder', {
                defaultValue: 'Context for the team - niche, goals, constraints…',
              })}
              rows={3}
              className="resize-none"
            />
          </section>
        </div>

        {/* ── Sticky Footer ── */}
        <div className="shrink-0 border-t border-gray-100 bg-white px-5 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-2 justify-end">
            <CancelButton onClick={handleClose} tooltip={t('fab_cancel', { defaultValue: 'Cancel' })} />
            <Button
              variant="primary"
              size="md"
              disabled={!canCreate || createMutation.isPending}
              loading={createMutation.isPending}
              onClick={handleCreate}
            >
              <i className="hm hm-plus-circle mr-1.5" style={{ fontSize: 14 }} aria-hidden="true" />
              {t('fab_create_brand_submit', { defaultValue: 'Create' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBrandSpaceModal;
